import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { billingService, BillingTier, BillingInterval } from '../services/billing.service';
import './CapabilityGate.css';

/**
 * Garde d'écran par capacité (saas/12, §4) :
 * - granted     → contenu normal.
 * - unlockable  → écran VISIBLE ET DÉSACTIVÉ avec l'appel à l'action Stripe :
 *                 l'écran verrouillé EST l'argumentaire.
 * - unavailable → masqué (retour au tableau de bord) : pas de verrou qu'on ne
 *                 peut pas acheter.
 */

/**
 * Catalogue des paliers vendus, aligné sur la vitrine (coloc-website
 * `config/pricing.ts`) et sur les six tarifs Stripe. Les CAPACITÉS, elles,
 * viennent de l'API — ceci n'est que l'affichage.
 *
 * Deux prix par palier : l'annuel est celui mis en avant, le mensuel porte
 * +20 %. L'écart laisse exactement deux mois offerts (12 ÷ 1,2 = 10 mois
 * payés), ce qui rend le libellé d'économie vrai sans le calculer.
 *
 * Exporté parce que « Mon compte » doit nommer et chiffrer le palier en cours :
 * une seconde copie de ces montants aurait dérivé au premier changement.
 */
export const TIERS: {
  tier: BillingTier;
  label: string;
  /** Prix mensuel en paiement mensuel, en euros. */
  monthly: number;
  /** Prix mensuel équivalent en paiement annuel — le prix d'appel. */
  yearlyMonthly: number;
  quota: string;
}[] = [
  { tier: 'solo', label: 'Solo', monthly: 23, yearlyMonthly: 19, quota: '1 colocation publiée' },
  { tier: 'trio', label: 'Trio', monthly: 54, yearlyMonthly: 45, quota: "Jusqu'à 3 colocations publiées" },
  { tier: 'illimite', label: 'Illimité', monthly: 95, yearlyMonthly: 79, quota: 'Colocations illimitées' },
];

/** Libellé du prix d'un palier, pour l'intervalle choisi. */
export const prixAffiche = (
  t: (typeof TIERS)[number],
  interval: BillingInterval,
): string => `${interval === 'yearly' ? t.yearlyMonthly : t.monthly} € / mois`;

export const UpgradeScreen: React.FC<{ title?: string }> = ({ title }) => {
  const [redirecting, setRedirecting] = useState<BillingTier | null>(null);
  const [error, setError] = useState<string | null>(null);
  // L'adresse doit être confirmée AVANT qu'on demande une carte. Le serveur
  // refuse désormais la session de paiement sans elle ; ici on évite d'y
  // envoyer l'utilisateur pour rien. La carte du tableau de bord posait déjà
  // cette condition, mais « Mon compte » mène aussi à cet écran sans la poser.
  const { data } = useCapabilities();
  const emailAConfirmer = data?.emailVerified === false;
  // Annuel par défaut, comme la grille de la vitrine : c'est l'offre mise en
  // avant, et son équivalent mensuel est le prix d'appel. Ouvrir sur le mensuel
  // afficherait d'emblée le plus cher des deux.
  const [interval, setInterval] = useState<BillingInterval>('yearly');

  const goToCheckout = async (tier: BillingTier) => {
    setRedirecting(tier);
    setError(null);
    try {
      // L'intervalle choisi part réellement au checkout : il était figé sur
      // 'monthly', si bien qu'un bailleur voyant le tarif annuel aurait
      // souscrit — et payé — au mensuel.
      const url = await billingService.createCheckoutSession(tier, interval);
      window.location.href = url;
    } catch (e) {
      // Le message du serveur plutôt qu'un « réessayez » générique : un refus
      // pour adresse non confirmée ne se résout pas en réessayant.
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail || 'Redirection vers le paiement impossible pour le moment. Réessayez.');
      setRedirecting(null);
    }
  };

  if (emailAConfirmer) {
    return (
      <div className="upgrade-screen">
        <div className="upgrade-card">
          <h2>Confirmez d&apos;abord votre adresse email</h2>
          <p>
            Un lien vous a été envoyé à l&apos;inscription. Une fois votre adresse
            confirmée, vous pourrez choisir votre offre et démarrer vos 14 jours
            d&apos;essai. Vous pouvez redemander ce lien depuis «&nbsp;Mon
            compte&nbsp;».
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="upgrade-screen">
      <div className="upgrade-card">
        <h2>{title || 'Cette fonctionnalité fait partie des offres payantes'}</h2>
        <p>
          Enregistrez votre carte pour démarrer vos <strong>14 jours d&apos;essai
          gratuit</strong> : publication de votre site, candidatures, contacts,
          espace locataire, accès agence… Aucun prélèvement avant la fin de
          l&apos;essai, résiliable à tout moment.
        </p>
        <div className="upgrade-switch" role="group" aria-label="Périodicité">
          <button
            type="button"
            className={interval === 'monthly' ? 'actif' : ''}
            onClick={() => setInterval('monthly')}
          >
            Mensuel
          </button>
          <button
            type="button"
            className={interval === 'yearly' ? 'actif' : ''}
            onClick={() => setInterval('yearly')}
          >
            Annuel <span className="upgrade-economie">2 mois offerts</span>
          </button>
        </div>
        <div className="upgrade-tiers">
          {TIERS.map((t) => (
            <div key={t.tier} className="upgrade-tier">
              <h3>{t.label}</h3>
              <p className="upgrade-price">{prixAffiche(t, interval)}</p>
              {/* Sans cette mention, « 19 € / mois » laisserait croire à un
                  prélèvement mensuel de 19 €, qui coûte en réalité 23. */}
              <p className="upgrade-facturation">
                {interval === 'yearly'
                  ? `soit ${t.yearlyMonthly * 12} € facturés annuellement`
                  : 'facturé mensuellement'}
              </p>
              <p className="upgrade-quota">{t.quota}</p>
              <button
                className="upgrade-cta"
                disabled={redirecting !== null}
                onClick={() => goToCheckout(t.tier)}
              >
                {redirecting === t.tier ? 'Redirection…' : 'Essayer 14 jours'}
              </button>
            </div>
          ))}
        </div>
        <p className="upgrade-sub">Prix TTC. Toutes les fonctionnalités sont incluses dans chaque palier.</p>
        {error && <p className="upgrade-error">{error}</p>}
      </div>
    </div>
  );
};

const CapabilityGate: React.FC<{ capability: string; children: React.ReactNode }> = ({
  capability,
  children,
}) => {
  const { loaded, stateOf } = useCapabilities();

  if (!loaded) {
    return <div className="loading">Chargement...</div>;
  }
  const state = stateOf(capability);
  if (state === 'granted') {
    return <>{children}</>;
  }
  if (state === 'unlockable') {
    return <UpgradeScreen />;
  }
  // unavailable : masqué — on ne montre pas un verrou qu'on ne peut pas acheter.
  return <Navigate to="/dashboard" replace />;
};

export default CapabilityGate;
